<script setup>
import { computed, ref, watch } from "vue";
import { getDocMeta, getYouTubeThumb, normalizeUrl } from "@/utils/noteLinks";
import { useLinkPreviews } from "@/composables/useLinkPreviews";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  // Lista de anexos:
  // [{ type: 'image' | 'youtube' | 'document' | 'website', url, extension, videoId }]
  attachments: { type: Array, default: () => [] },
});

// Veredito do backend (POST /LinkPreview). Site e documento dependem dele;
// imagem e vídeo detectam a falha sozinhos, pelo onerror do <img>.
const { linkStatus } = useLinkPreviews();

const currentStatus = computed(() =>
  current.value ? linkStatus[normalizeUrl(current.value.url)] : undefined
);
const isBroken = computed(() => currentStatus.value?.state === "broken");
const siteImage = computed(() => currentStatus.value?.imageUrl || null);

const emit = defineEmits(["close"]);

const currentIndex = ref(0);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

const total = computed(() => props.attachments.length);
const hasMultiple = computed(() => total.value > 1);
const current = computed(() => props.attachments[currentIndex.value] ?? null);

// Reinicia no primeiro item sempre que a lista mudar (ou o modal reabrir).
watch(
  () => props.attachments,
  () => {
    currentIndex.value = 0;
  }
);

function goPrev() {
  if (!total.value) return;
  currentIndex.value =
    (currentIndex.value - 1 + total.value) % total.value;
}

function goNext() {
  if (!total.value) return;
  currentIndex.value = (currentIndex.value + 1) % total.value;
}

function getHostname(url) {
  const m = String(url).match(/^https?:\/\/([^/]+)/i);
  return m ? m[1].replace(/^www\./i, "") : String(url);
}

function faviconUrl(url) {
  return (
    `https://www.google.com/s2/favicons?domain=` +
    `${encodeURIComponent(getHostname(url))}&sz=64`
  );
}

function typeLabel(item) {
  if (!item) return "";
  if (item.type === "image") return "Imagem";
  if (item.type === "youtube") return item.videoId ? "Vídeo" : "YouTube";
  if (item.type === "website") return "Site";
  return item.extension ? item.extension.toUpperCase() : "Documento";
}

// Ícone do estado "quebrado", específico por tipo.
function brokenIcon(item) {
  if (item?.type === "image") return "fas fa-image";
  if (item?.type === "youtube") return "fas fa-video-slash";
  if (item?.type === "document") return "fas fa-file-excel";
  return "fas fa-globe";
}

// Ícone do documento conforme a extensão (pdf, doc, xls…)
function docIcon(item) {
  return `fas ${getDocMeta(item?.extension).icon}`;
}
function docColor(item) {
  return getDocMeta(item?.extension).color;
}

function youtubeThumb(item) {
  return item?.videoId ? getYouTubeThumb(item.videoId) : "";
}

// Imagem/vídeo que não carrega: esconde e revela o cartão de link quebrado.
function handleMediaError(event) {
  event.target.style.display = "none";
  const fallback = event.target.nextElementSibling;
  if (fallback) fallback.style.display = "flex";
}

// Vídeo removido: o YouTube responde 200 com um placeholder cinza de 120x90 em vez
// de 404, então o @error nunca dispara. A miniatura de um vídeo vivo tem 480px.
function handleYoutubeLoad(event) {
  if (event.target.naturalWidth <= 120) handleMediaError(event);
}

// Favicon que não carrega: esconde e revela o ícone de globo ao lado.
function handleFaviconError(event) {
  event.target.style.display = "none";
  const fallback = event.target.nextElementSibling;
  if (fallback) fallback.style.display = "flex";
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="attachment-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">
          <i class="fas fa-paperclip header-icon"></i>
          <span>{{ hasMultiple ? "Anexos" : "Anexo" }}</span>
          <span v-if="hasMultiple" class="header-counter">
            {{ currentIndex + 1 }} / {{ total }}
          </span>
        </h3>

        <button
          type="button"
          class="modal-close-btn"
          @click="emit('close')"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="current" class="attachment-body">
        <div class="attachment-item-head">
          <span
            class="attachment-type-badge"
            :class="{
 'attachment-type-badge--image': current.type === 'image',
              'attachment-type-badge--youtube': current.type === 'youtube',
              'attachment-type-badge--website': current.type === 'website',
              'attachment-type-badge--doc': current.type === 'document',
            }"
          >
            <i
              :class="{
 'fas fa-image': current.type === 'image',
                'fab fa-youtube': current.type === 'youtube',
                'fas fa-globe': current.type === 'website',
                'fas fa-file-alt': current.type === 'document',
              }"
            ></i>
            {{ typeLabel(current) }}
          </span>

          <span v-if="isBroken" class="attachment-broken-badge">
            <i class="fas fa-exclamation-triangle"></i>
            Link quebrado
          </span>
        </div>

        <!-- Quebrado (veredito do backend): ícone do tipo, em vermelho -->
        <div
          v-if="isBroken"
          class="attachment-preview-wrap attachment-preview-doc attachment-preview-broken"
        >
          <i :class="brokenIcon(current)" class="attachment-doc-icon"></i>
        </div>

        <!-- Imagem -->
        <div
          v-else-if="current.type === 'image'"
          class="attachment-preview-wrap"
        >
          <img
            :key="current.url"
            :src="current.url"
            class="attachment-preview-img"
            alt="Pré-visualização da imagem"
            @error="handleMediaError"
          />
          <span class="attachment-media-fallback" style="display: none">
            <i class="fas fa-image attachment-doc-icon attachment-icon-broken"></i>
            <span class="attachment-fallback-text">Link quebrado</span>
          </span>
        </div>

        <!-- Vídeo do YouTube -->
        <div
          v-else-if="current.type === 'youtube' && current.videoId"
          class="attachment-preview-wrap"
        >
          <img
            :key="current.url"
            :src="youtubeThumb(current)"
            class="attachment-preview-img"
            alt="Pré-visualização do vídeo"
            @error="handleMediaError"
            @load="handleYoutubeLoad"
          />
          <span class="attachment-media-fallback" style="display: none">
            <i
              class="fas fa-video-slash attachment-doc-icon attachment-icon-broken"
            ></i>
            <span class="attachment-fallback-text">Link quebrado</span>
          </span>
        </div>

        <!-- Página do YouTube (canal/playlist): preview OG do backend -->
        <div
          v-else-if="current.type === 'youtube' && siteImage"
          class="attachment-preview-wrap"
        >
          <img
            :key="siteImage"
            :src="siteImage"
            class="attachment-preview-img"
            alt="Pré-visualização da página"
            @error="handleMediaError"
          />
          <span class="attachment-media-fallback" style="display: none">
            <i class="fab fa-youtube attachment-doc-icon" style="color: #ff0000"></i>
          </span>
        </div>
        <!-- YouTube sem vídeo e sem OG: favicon do YouTube (ícone de vídeo, não globo) -->
        <div
          v-else-if="current.type === 'youtube'"
          class="attachment-preview-wrap attachment-preview-doc"
        >
          <img
            :key="current.url"
            :src="faviconUrl(current.url)"
            class="attachment-favicon"
            alt="Favicon do YouTube"
            @error="handleFaviconError"
          />
          <span class="attachment-favicon-fallback" style="display: none">
            <i class="fab fa-youtube attachment-doc-icon" style="color: #ff0000"></i>
          </span>
        </div>

        <!-- Site: imagem OpenGraph quando o backend a fornece; senão, favicon -->
        <div
          v-else-if="current.type === 'website' && siteImage"
          class="attachment-preview-wrap"
        >
          <img
            :key="siteImage"
            :src="siteImage"
            class="attachment-preview-img"
            alt="Pré-visualização do site"
            @error="handleMediaError"
          />
          <span class="attachment-media-fallback" style="display: none">
            <i class="fas fa-globe attachment-doc-icon"></i>
          </span>
        </div>
        <div
          v-else-if="current.type === 'website'"
          class="attachment-preview-wrap attachment-preview-doc"
        >
          <img
            :key="current.url"
            :src="faviconUrl(current.url)"
            class="attachment-favicon"
            alt="Favicon do site"
            @error="handleFaviconError"
          />
          <span class="attachment-favicon-fallback" style="display: none">
            <i class="fas fa-globe attachment-doc-icon"></i>
          </span>
        </div>

        <!-- Documento: ícone conforme a extensão -->
        <div v-else class="attachment-preview-wrap attachment-preview-doc">
          <i
            :class="docIcon(current)"
            class="attachment-doc-icon"
            :style="{ color: docColor(current) }"
          ></i>
        </div>

        <p v-if="currentStatus?.title" class="attachment-title">
          {{ currentStatus.title }}
        </p>

        <a
          :href="current.url"
          target="_blank"
          rel="noopener noreferrer"
          class="attachment-link"
          :class="{ 'attachment-link--broken': isBroken }"
        >
          <i class="fas fa-external-link-alt"></i>
          <span>{{ current.url }}</span>
        </a>

        <!-- >>> Navegação entre anexos (voltar/avançar) -->
        <div v-if="hasMultiple" class="attachment-nav">
          <button
            type="button"
            class="nav-btn"
            @click="goPrev"
            aria-label="Anexo anterior"
          >
            <i class="fas fa-chevron-left"></i>
            <span>Anterior</span>
          </button>

          <span class="nav-indicator">{{ currentIndex + 1 }} / {{ total }}</span>

          <button
            type="button"
            class="nav-btn"
            @click="goNext"
            aria-label="Próximo anexo"
          >
            <span>Próximo</span>
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.attachment-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-icon {
  color: var(--warning);
  font-size: 0.95rem;
}

.header-counter {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
}

.attachment-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attachment-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.attachment-broken-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 9999px;
  background-color: var(--danger-bg);
  color: var(--danger-text);
  border: 1px solid var(--danger-bg);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.attachment-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.attachment-type-badge--image {
  background-color: var(--info-bg);
  color: var(--link-hover);
}

.attachment-type-badge--doc {
  background-color: var(--warning-bg);
  color: #b45309;
}

.attachment-type-badge--website {
  background-color: #f0fdf4;
  color: #15803d;
}

.attachment-type-badge--youtube {
  background-color: var(--danger-bg);
  color: var(--danger-hover);
}

.attachment-preview-wrap {
  width: 100%;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background-color: var(--surface-2);
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.attachment-preview-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* Cartão de link quebrado (imagem/vídeo que falharam, ou veredito do backend) */
.attachment-preview-broken {
  border-color: var(--danger-bg);
  background-color: var(--danger-bg);
}

.attachment-media-fallback {
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: var(--danger-bg);
}

.attachment-icon-broken {
  color: var(--danger-hover);
  opacity: 0.6;
}

.attachment-fallback-text {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--danger-text);
}

.attachment-title {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-dark);
  overflow-wrap: anywhere;
}

.attachment-link--broken {
  background-color: var(--danger-bg);
  color: var(--danger-text);
}

.attachment-link--broken:hover {
  background-color: var(--danger-bg);
}

.attachment-preview-doc {
  padding: 24px;
}

.attachment-doc-icon {
  font-size: 2.5rem;
  color: #a99fd6;
}

.attachment-favicon {
  width: 96px;
  height: 96px;
  object-fit: contain;
}

.attachment-favicon-fallback {
  align-items: center;
  justify-content: center;
}

.attachment-link {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background-color: var(--info-bg);
  color: var(--link-hover);
  font-size: 0.82rem;
  text-decoration: none;
  transition: background-color 0.15s ease;
}

.attachment-link:hover {
  background-color: var(--info-border);
}

.attachment-link i {
  flex-shrink: 0;
  font-size: 0.75rem;
}

/* Mantém o link em uma única linha (altura fixa do modal) */
.attachment-link span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* >>> Navegação entre anexos */
.attachment-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px dashed var(--border-color);
}

.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background-color: var(--surface-2);
  color: var(--text-dark);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    transform 0.05s ease;
}

.nav-btn:hover {
  background-color: var(--neutral-100);
  border-color: var(--border-color);
}

.nav-btn:active {
  transform: translateY(1px);
}

.nav-btn i {
  font-size: 0.7rem;
}

.nav-indicator {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
}
</style>
